import Card from "@/UI/Card";
import { useGetDealQuery, useUpdateDealMutation } from "@/redux/rtk/features/CRM/deal/dealApi";
import { useGetDealStagesQuery } from "@/redux/rtk/features/CRM/dealStage/dealStageApi";
import { useGetContactStatusesQuery } from "@/redux/rtk/features/CRM/contactStatus/contactStatusApi";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import DealStageChanger from "./DealStageChanger";
import DealInformation from "./DealInformation";
import DealStageHistory from "./DealStageHistory";
import Tabs, { Tab } from "@/UI/Tabs";
import Notes from "@/components/CommonUi/Notes";
import StudentAttachments from "@/components/CRM/StudentAttachments/StudentAttachments";
import LeadApplications from "@/components/Lead/Leads/LeadApplications";
import { FaStickyNote, FaPaperclip, FaHistory, FaFileAlt, FaTasks } from "react-icons/fa";
import DealTasks from "./DealTasks";
import usePermissions from "@/utils/usePermissions";

export default function DealProfile({ deal: dealProp, onUpdate }) {
  const { t } = useTranslation();
  const { permissions } = usePermissions();
  const canReadCommission = permissions?.includes("read-commission");
  const { id } = useParams();

  // Use prop if provided, otherwise fetch
  const {
    data: dealData,
    isLoading: dealLoading,
    refetch: refetchDeal,
  } = useGetDealQuery(id, {
    skip: !!dealProp || !id,
    refetchOnMountOrArgChange: true,
  });

  const deal = dealProp || dealData;
  const handleRefetch = onUpdate || refetchDeal;

  // Load deal stages and contact statuses (only once, no refetch)
  const { data: dealStages, isLoading: stagesLoading } = useGetDealStagesQuery(undefined, {
    refetchOnMountOrArgChange: false,
    refetchOnFocus: false,
    refetchOnReconnect: false,
  });

  const { data: contactStatuses, isLoading: statusesLoading } = useGetContactStatusesQuery(undefined, {
    refetchOnMountOrArgChange: false,
    refetchOnFocus: false,
    refetchOnReconnect: false,
  });

  if (dealLoading || stagesLoading || statusesLoading || !deal) {
    return null; // Parent component handles loading
  }

  return (
    <div className="w-full">
      <div className="flex flex-col w-full p-6 gap-6">
        {/* Stage Changer */}
        <DealStageChanger
          dealStages={dealStages}
          currentDeal={deal}
          onStageChange={handleRefetch}
        />

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Information Column */}
          <div className="lg:col-span-1 border-r border-gray-100 dark:border-gray-700 pr-0 lg:pr-6">
            <DealInformation
              data={deal}
              loading={dealLoading}
              contactStatuses={contactStatuses}
              onUpdate={handleRefetch}
            />
          </div>

          {/* Main Content Columns */}
          <div className="lg:col-span-1">
            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm overflow-hidden">
              <Tabs>
                <Tab label={t("common.notes")} icon={<FaStickyNote />}>
                  <div className="pt-4">
                    <Notes
                      data={deal}
                      loading={dealLoading}
                      name={"dealId"}
                      singleLoadThunk={handleRefetch}
                    />
                  </div>
                </Tab>

                <Tab label={t("application.title")} icon={<FaFileAlt />}>
                  <div className="pt-4">
                    <LeadApplications
                      lead={{ ...deal, dealId: deal.id }}
                      onUpdate={handleRefetch}
                      isAgent={false}
                    />
                  </div>
                </Tab>

                <Tab label={t("common.attachments")} icon={<FaPaperclip />}>
                  <div className="pt-4">
                    <StudentAttachments
                      student={deal}
                      onUpdate={handleRefetch}
                      studentType="deal"
                      studentId={deal?.id}
                      leadId={deal?.leadId}
                    />
                  </div>
                </Tab>

                <Tab label={t("deal.stageHistory")} icon={<FaHistory />}>
                  <div className="pt-4">
                    <DealStageHistory
                      deal={deal}
                      dealStages={dealStages}
                    />
                  </div>
                </Tab>

                <Tab label={t("task.title")} icon={<FaTasks />}>
                  <div className="pt-4">
                    <DealTasks dealId={deal?.id} />
                  </div>
                </Tab>

              </Tabs>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

